import { forwardRef, memo, useCallback, useEffect, useRef, useState } from 'react';
import type { ComponentPropsWithoutRef, ReactElement } from 'react';

import { cx } from '@/utils/cx';

import { useActionContext } from '../BottomNavigationContext';

import styles from './BottomNavigationAction.module.scss';

export interface BottomNavigationActionProps extends Omit<ComponentPropsWithoutRef<'button'>, 'children' | 'id'> {
  /** 탭 식별자 — onAction 인자, data-tab attribute */
  id: string;
  /** 비활성 아이콘 */
  icon: ReactElement;
  /** 활성 아이콘 (미지정 시 icon 사용) */
  activeIcon?: ReactElement;
  /** 아이콘 하단 텍스트 */
  label: string;
  /** 활성 상태 */
  active?: boolean;
  /** onAction 콜백에 전달할 메타데이터 */
  meta?: Record<string, unknown>;
  /**
   * 노출(impression) 트래킹 콜백. button element 가 viewport 와 intersect 할 때마다 호출.
   *
   * **re-impression 패턴** — 한 번 노출 후 다시 비노출 → 재 노출 시 또 호출됨.
   * - 첫 viewport 진입: ✅ 호출
   * - viewport 이탈: ❌ 호출 안 함 (leave 콜백 아님)
   * - 재 진입 (스크롤로 돌아옴 등): ✅ 호출
   *
   * `display:none` 상태에서는 IntersectionObserver 가 entry 를 deliver 하지 않으므로 (브라우저 spec)
   * 자동 비활성. 호스트 추가 가드 불필요.
   *
   * `threshold: 0` 고정. fine-grained 옵션이 필요하면 호스트가 자체 IntersectionObserver 를
   * ref 로 부착.
   */
  onImpressed?: () => void;
}

type PressPhase = 'idle' | 'pressed' | 'released';

const PRESS_PHASE_ICON_CLASS: Record<PressPhase, string | undefined> = {
  idle: undefined,
  pressed: styles.iconPressed,
  released: styles.iconReleased,
};

// 피크(26px) 유지 후 idle 복귀 — pop(0.1s) 이후 최소 여유
const RELEASE_DURATION = 120;

export const BottomNavigationAction = memo(
  forwardRef<HTMLButtonElement, BottomNavigationActionProps>(
    (
      {
        id,
        icon,
        activeIcon,
        label,
        active,
        meta = {},
        onClick,
        onImpressed,
        className,
        'aria-label': ariaLabel,
        ...rest
      },
      ref,
    ) => {
      const { onAction } = useActionContext();
      const [pressPhase, setPressPhase] = useState<PressPhase>('idle');
      const timerRef = useRef<ReturnType<typeof setTimeout>>();

      // IntersectionObserver target — button element. forwardRef 의 외부 ref 와 동시 set.
      const buttonRef = useRef<HTMLButtonElement | null>(null);
      const onImpressedRef = useRef(onImpressed);
      onImpressedRef.current = onImpressed;

      const setRefs = useCallback(
        (node: HTMLButtonElement | null) => {
          buttonRef.current = node;
          if (typeof ref === 'function') ref(node);
          else if (ref) ref.current = node;
        },
        [ref],
      );

      useEffect(
        () => () => {
          clearTimeout(timerRef.current);
        },
        [],
      );

      useEffect(() => {
        if (!onImpressed) return undefined;
        if (typeof IntersectionObserver === 'undefined') return undefined;
        const el = buttonRef.current;

        if (!el) return undefined;

        const observer = new IntersectionObserver(([entry]) => {
          if (entry.isIntersecting) onImpressedRef.current?.();
        });
        observer.observe(el);
        return () => {
          observer.disconnect();
        };
        // onImpressed 가 truthy 인 동안 mount 1회만 동작. 호스트가 mount 후 추가/제거하는 케이스는 미지원.
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, []);

      const press = useCallback(() => {
        clearTimeout(timerRef.current);
        setPressPhase('pressed');
      }, []);

      const release = useCallback(() => {
        setPressPhase('released');
        timerRef.current = setTimeout(() => {
          setPressPhase('idle');
        }, RELEASE_DURATION);
      }, []);

      const cancel = useCallback(() => {
        clearTimeout(timerRef.current);
        setPressPhase('idle');
      }, []);

      const handleKeyDown = useCallback(
        (e: React.KeyboardEvent) => {
          if (e.key === 'Enter' || e.key === ' ') press();
        },
        [press],
      );

      const handleKeyUp = useCallback(
        (e: React.KeyboardEvent) => {
          if (e.key === 'Enter' || e.key === ' ') release();
        },
        [release],
      );

      const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
        onClick?.(e);

        if (!e.defaultPrevented) {
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
          if (navigator.vibrate) navigator.vibrate(200); // Safari 에서는 navigator.vibrate 가 없음 (무시)
          onAction?.(id, meta, e);
        }
      };

      const displayIcon = active && activeIcon ? activeIcon : icon;

      return (
        <button
          aria-current={active ? 'page' : undefined}
          aria-label={ariaLabel ?? label}
          className={cx(styles.action, className)}
          data-tab={id}
          onClick={handleClick}
          onKeyDown={handleKeyDown}
          onKeyUp={handleKeyUp}
          onPointerDown={press}
          onPointerLeave={cancel}
          onPointerUp={release}
          ref={setRefs}
          type="button"
          {...rest}
        >
          <div className={cx(styles.actionInner, pressPhase === 'pressed' && styles.actionInnerPressed)}>
            <div className={cx(styles.iconWrapper, PRESS_PHASE_ICON_CLASS[pressPhase])}>{displayIcon}</div>
            <span className={cx(styles.label, active && styles.labelActive)}>{label}</span>
          </div>
        </button>
      );
    },
  ),
);

BottomNavigationAction.displayName = 'BottomNavigation.Action';
